import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { OfflineBadge } from '@/components/OfflineBadge';
import { ALL_NAV, CONTENT_BOTTOM_CLEARANCE, MobileBottomNav } from '@/app/MobileBottomNav';
import { VersionMenuLine } from '@/app/version';

/** ~6px below status bar in standalone PWA; env() is 0 in desktop Safari tab. */
const MOBILE_HEADER_PT = 'calc(env(safe-area-inset-top, 0px) + 6px)';

export function AppShell() {
  const { user, logout } = useAuth();

  return (
    <div className="flex min-h-[100dvh] min-h-[100vh] flex-col md:flex-row bg-slate-50 overflow-x-hidden max-w-full">
      <aside className="hidden md:flex md:w-56 md:flex-col bg-slate-900 text-slate-100">
        <div className="px-5 py-4 border-b border-slate-800">
          <div className="text-lg font-semibold">JLT POS</div>
          <div className="text-xs text-slate-400 truncate">{user?.username}</div>
        </div>
        <nav className="flex-1 py-3">
          {ALL_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm hover:bg-slate-800 ${
                  isActive ? 'bg-slate-800 text-white' : 'text-slate-300'
                }`
              }
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
              </svg>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 pb-3">
          <VersionMenuLine tone="dark" />
        </div>
        <div className="border-t border-slate-800 p-3">
          <button onClick={logout} className="w-full btn-secondary text-slate-800">
            Sign out
          </button>
        </div>
      </aside>

      <header
        className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-white/60 bg-white/85 px-4 pb-2 text-slate-900 shadow-[0_1px_0_rgba(255,255,255,0.75)_inset] supports-[backdrop-filter]:bg-white/55 supports-[backdrop-filter]:backdrop-blur-2xl supports-[backdrop-filter]:backdrop-saturate-150"
        style={{ paddingTop: MOBILE_HEADER_PT }}
      >
        <p className="min-w-0 truncate text-sm font-semibold leading-tight">
          <span>JLT POS</span>
          {user?.username ? (
            <>
              <span className="mx-1.5 font-normal text-slate-400" aria-hidden>
                ·
              </span>
              <span className="font-normal text-slate-600">{user.username}</span>
            </>
          ) : null}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <OfflineBadge />
          <button
            onClick={logout}
            className="text-xs text-slate-600 px-2 py-1 rounded-md hover:bg-slate-900/5 active:bg-slate-900/10"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 min-w-0 max-w-full overflow-x-hidden overscroll-x-none touch-pan-y">
        <div className="hidden md:flex md:justify-end md:px-6 md:pt-3">
          <OfflineBadge />
        </div>
        <Outlet />
        {/* Phone only: room under every page for the floating nav (inline, not a CSS utility). */}
        <div className="md:hidden" aria-hidden style={{ height: CONTENT_BOTTOM_CLEARANCE }} />
      </main>

      <MobileBottomNav />
    </div>
  );
}
